import { useContext, Fragment } from "react";

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import EncapsulantPhaseContext from "../../context/materialcapture/EncapsulantPhaseContext";

import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import SendSharpIcon from '@mui/icons-material/SendSharp';
import Box from "@mui/material/Box";
import Typography from '@mui/material/Typography';

import FormulationPicker from "./FormulationPicker";

const EncapsulantPhaseForm = () => {
  const { 
    encapsulantPhaseData, setEncapsulantPhaseData, handleSubmit, 
    initialValues, formErrors, handleLipidStockIdChange, getLipids
  } = useContext(EncapsulantPhaseContext);

  return(
    <>
      <Grid container spacing={1} mt={1}>
        <Grid item xs={12}>
          <FormulationPicker getLipids={getLipids} showApplyBtn={true} />
        </Grid>

        <Grid item xs={6}>
          <Autocomplete
            value={encapsulantPhaseData?.solvent}
            onChange={(e, newValue) => 
              setEncapsulantPhaseData(prev => ({
                ...prev,
                solvent: newValue
              }))
            }
            onInputChange={(e, newValue) =>
              setEncapsulantPhaseData(prev => ({
                ...prev,
                solvent: newValue
              }))
            }
            options={initialValues?.solventArray}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Solvent *" 
                placeholder="Type to search Solvent..." 
                error={'solvent' in formErrors}
                helperText={formErrors['solvent']}
              />
            )}
            freeSolo
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <Autocomplete
            value={encapsulantPhaseData?.operatorArray || []}
            onChange={(e, newValue) => 
              setEncapsulantPhaseData(prev => ({
                ...prev,
                operatorArray: newValue
              }))
            }
            onInputChange={(e, newInputValue, reason) => {
              if (reason === "createOption" && newInputValue.trim()) {
                setEncapsulantPhaseData((prev) => ({
                  ...prev,
                  operatorArray: [
                    ...(prev.operatorArray || []),
                    newInputValue
                  ],
                }));
              }
            }}
            options={initialValues?.operatorArray || []}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Operator *" 
                placeholder="Type to search Operator..." 
                error={'operator' in formErrors}
                helperText={formErrors['operator']}
              />
            )}
            multiple
            freeSolo
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='Date prepared *'
                name={'datePrepared'}
                value={encapsulantPhaseData?.datePrepared} 
                onChange={e => 
                  setEncapsulantPhaseData(prev => ({
                    ...prev,
                    datePrepared: e
                  }))
                } 
                maxDate={initialValues?.today}
                renderInput={(params) => 
                  <TextField 
                    {...params} 
                    error={Boolean(formErrors['datePrepared'])}
                    helperText={formErrors['datePrepared']}
                  />
                }
              />
            </LocalizationProvider>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{display: 'flex', mt: 1}}>
        <Typography
          color='text.secondary'
          variant='h6'
        >
          <strong>Lipids: {encapsulantPhaseData?.lipidArray.length}</strong>
        </Typography>
      </Box>

      <Grid container spacing={1} mt={1}>
        {encapsulantPhaseData?.lipidArray.map((data, index) => (
          <Fragment key={index}>
            <Grid item xs={6}>
              <TextField
                varient="outlined"
                label="Lipid Name *"
                name={'lipidName'}
                value={data?.lipid_name}
                helperText={formErrors[`lipid_name-${index}`]}
                error={`lipid_name-${index}` in formErrors}
                disabled
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <Autocomplete
                value={data?.unique_lipid_stock_id}
                onChange={(e, newValue) => 
                  handleLipidStockIdChange(index, newValue)
                }
                options={initialValues?.lipidStocksArray}
                getOptionLabel={(option) => option?.unique_lipid_stock_id}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Lipid Stock ID *" 
                    placeholder="Type to search Lipid Stock ID..." 
                    error={`unique_lipid_stock_id-${index}` in formErrors}
                    helperText={formErrors[`unique_lipid_stock_id-${index}`]}
                  />
                )}
                freeSolo
                fullWidth
              />
            </Grid>
          </Fragment>
        ))}
        

        <Grid item xs={12}>
          <Stack direction="row" spacing={1} justifyContent="flex-end" mr={1}>
            <Button 
              variant="contained" 
              endIcon={<SendSharpIcon />} 
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
};

export default EncapsulantPhaseForm